import { Autocomplete, InputAdornment, Stack, Switch, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PercentIcon from '@mui/icons-material/Percent';
import Grid from '@mui/material/Grid';
import React from "react";

interface Props {
  handleChange: any;
  siteAllAvailableWidgets: any;
  webSiteValues: any;
  superUser: any;
  tierList: any;
  formError: any;
  setFormError: any;
}

function Features({ siteAllAvailableWidgets, tierList, superUser, webSiteValues, handleChange, formError,setFormError }: Props) {
  //-- Debug Field Checkbox value set
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    handleChange("theme_debug", isChecked ? 1 : 0); // Update state: 1 if checked, 0 if not
  };

  //-- Percentage Validation
  const validatePercentage = (value : any) => {
    const regex = /^(100|[0-9]{1,2})$/; // Matches integers 0-100
    return regex.test(value);
};

  //-- OnChange Data set in state 
  const handleInputChange = (name: string, value: any) => {
    if (name === "rush_pick_effect_percentage") {
      const intValue = Number(value); // Convert to integer
      if (!isNaN(intValue)) {
        handleChange(name, intValue); // Pass the integer value to handleChange
      }else{
        handleChange(name, null);
      }
      return; // Exit early since we've handled this case
    }

    if (name === "rush_pick_latency") {
      if (!/^-?\d*$/.test(value)) {
        return; // Prevent invalid characters from being entered
      }
      const intValue = Number(value); // Convert to integer
      if (!isNaN(intValue)) {
        handleChange(name, intValue); // Pass the integer value to handleChange
      }
      // else{
      //   handleChange(name, null);
      //   setFormError({...formError, [name] : 'Appearance latency is required and can not be zero.'});
      // }
      return;
    }

    if (name === "rush_pick_min_position") {
      if (!/^-?\d*$/.test(value)) {
        return; // Prevent invalid characters from being entered
      }
      const intValue = Number(value); // Convert to integer
      if (!isNaN(intValue) && intValue > 0) {
        if (webSiteValues?.rush_pick_max_position && intValue > webSiteValues?.rush_pick_max_position) {
          handleChange(name, null)
          setFormError({...formError, [name] : 'Must be less than or equal to the Maximal Position.'});
        }else{
          handleChange(name, intValue); // Pass the integer value to handleChange
        } // Pass the integer value to handleChange
      }else{
        handleChange(name, null);
      }
      return; // Exit early since we've handled this case
    }

    if (name === "rush_pick_max_position") {
      if (!/^-?\d*$/.test(value)) {
        return; // Prevent invalid characters from being entered
      }
      const intValue = Number(value); // Convert to integer
      if (!isNaN(intValue) && intValue > 0) {
        if (webSiteValues?.rush_pick_min_position && intValue < webSiteValues?.rush_pick_min_position) {
          handleChange(name, null)
          setFormError({...formError, [name] : 'Must be greater than or equal to the Minimal Position.'});
        }else{
          handleChange(name, intValue); // Pass the integer value to handleChange
        } // Pass the integer value to handleChange
      }else{
        handleChange(name, null)
      }
      return; // Exit early since we've handled this case
    }

    // if osano_cm_link is empty, then pass null
    if (name === "osano_cm_link" && !value) {
      handleChange(name, null);
      return;
    }

    // if gtm_id is empty, then pass null
    if (name === "gtm_id" && !value) {
      handleChange(name, null);
      return;
    }

    // if gtm_id has value, remove all spaces and convert to uppercase
    if (name === "gtm_id" && value) {
      const cleanedUpperValue = value.replace(/\s+/g, '').toUpperCase();
      // Check if GTM ID has three or more consecutive zeros after "GTM-"
      if (cleanedUpperValue.startsWith('GTM-') && /^GTM-000/.test(cleanedUpperValue)) {
        setFormError({...formError, [name] : 'GTM ID cannot have three or more consecutive zeros after GTM-'});
        return; // Prevent invalid GTM IDs with three or more consecutive zeros at start
      }
      
      // Clear any existing error for this field
      if (formError?.[name]) {
        const newFormError = { ...formError };
        delete newFormError[name];
        setFormError(newFormError);
      }
      
      handleChange(name, cleanedUpperValue);
      return;
    }

    // if tracking_domain is empty, then pass null
    if (name === "tracking_domain" && !value) {
      handleChange(name, null);
      return;
    }

    // For other fields, just pass the value as is
    handleChange(name, value);
  };

  //-- Get Popup Offer Widget
  const filteredWidgets = siteAllAvailableWidgets?.filter(
    (widget: any) => widget?.type === "Popup Offer"
  ) || [];

  return (
    <>
      <Typography>Features</Typography>
      <Box mt={1}>
        <Typography fontWeight={500}>Animation</Typography>
        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              required
              id="rush_pick_effect_percentage"
              name={"rush_pick_effect_percentage"}
              label="Appearance Percentage"
              error={formError?.rush_pick_effect_percentage}
              helperText={
                formError?.rush_pick_effect_percentage
              }
              value={webSiteValues?.rush_pick_effect_percentage}
              color="secondary"
              onChange={(e) => {
                const value = e.target.value;
                if (validatePercentage(value) || value === "") {
                  handleInputChange("rush_pick_effect_percentage", value); // Only update if valid
                }
              }}
              InputLabelProps={{
                shrink: true, // This will keep the label above the input field
              }}

              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <PercentIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton disabled={webSiteValues?.rush_pick_effect_percentage === 0} onClick={() => handleInputChange("rush_pick_effect_percentage", webSiteValues?.rush_pick_effect_percentage - 1)} aria-label="Minus" color="secondary" size="small" sx={{ padding: "0" }}>
              <RemoveIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton disabled={webSiteValues?.rush_pick_effect_percentage === 100} onClick={() => handleInputChange("rush_pick_effect_percentage", webSiteValues?.rush_pick_effect_percentage + 1)} aria-label="Add" color="secondary" size="small" sx={{ padding: "0" }}>
              <AddIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              required
              value={webSiteValues?.rush_pick_latency === null ? "" : webSiteValues?.rush_pick_latency}
              id="outlined-required"
              error={formError?.rush_pick_latency} //-- red border if empty
              helperText={formError?.rush_pick_latency}
              onChange={(e) => handleInputChange("rush_pick_latency", e.target.value)}
              name="rush_pick_latency"
              label="Appearance Latency"
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={2}>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              required
              error={formError?.rush_pick_min_position} //-- red border if empty
              helperText={formError?.rush_pick_min_position}
              value={webSiteValues?.rush_pick_min_position === null ? "" : webSiteValues?.rush_pick_min_position}
              onChange={(e) => handleInputChange("rush_pick_min_position", e.target.value)}
              id="outlined-required"
              name={"rush_pick_min_position"}
              label="Minimal Position"
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              required
              error={formError?.rush_pick_max_position} //-- red border if empty
              helperText={formError?.rush_pick_max_position}
              value={webSiteValues?.rush_pick_max_position === null ? "" : webSiteValues?.rush_pick_max_position}
              onChange={(e) => handleInputChange("rush_pick_max_position", e.target.value)}
              id="outlined-required"
              name={"rush_pick_max_position"}
              label="Maximal Position"
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
        <Typography mt={1} fontWeight={500}>Special Offer</Typography>
        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={1}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={
                siteAllAvailableWidgets.length > 0
                  ? filteredWidgets.map((widget: any) => ({
                    widget_name: widget?.widget_name,
                    shared_uid: widget?.shared_uid,
                  }))
                  : []
              }
              getOptionLabel={(option: any) => option.widget_name || ""}
              value={
                filteredWidgets?.find(
                  (widget: any) => widget.shared_uid === webSiteValues?.special_offer_shared_uid
                ) || null
              }
              onChange={(_, newValue: any) => {
                handleInputChange("special_offer_shared_uid", newValue?.shared_uid || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Popup Name"
                  name={"special_offer_shared_uid"}
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={
                tierList.length > 0
                  ? tierList.map((tier: any) => ({
                    label: tier?.label,
                    shared_uid: tier?.shared_uid,
                  }))
                  : []
              }
              getOptionLabel={(option: any) => option.label || ""}
              value={
                tierList.find((tier: any) => tier.shared_uid === webSiteValues.special_offer_tier_id) || null
              }
              onChange={(_, newValue: any) => {
                handleInputChange("special_offer_tier_id", newValue?.shared_uid || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Starting Tier"
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              size="small"
            />
          </Grid>
        </Grid>
        <Typography mt={1} fontWeight={500}>Optionals</Typography>
        <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={1}>
          <Grid item xs={12} md={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <TextField
                id="outlined-multiline-flexible"
                label="GTM ID"
                fullWidth
                name={"gtm_id"}
                error={!!formError?.gtm_id}
                helperText={formError?.gtm_id}
                onChange={(e) =>
                  handleInputChange("gtm_id", e.target.value)
                }
                value={webSiteValues?.gtm_id || ""}
                color="secondary"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                id="outlined-multiline-flexible"
                label="Osano CM Link" // Set your label here
                fullWidth
                name={"osano_cm_link"}
                onChange={(e) =>
                  handleInputChange("osano_cm_link", e.target.value)
                }
                value={webSiteValues?.osano_cm_link || ""}
                color="secondary"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                id="outlined-multiline-flexible"
                label="Tracking Domain" // Set your label here
                fullWidth
                name={"tracking_domain"}
                value={webSiteValues?.tracking_domain || ""}
                onChange={(e) =>
                  handleInputChange("tracking_domain", e.target.value)
                }
                color="secondary"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <Box mt={2}>
          {superUser && <Stack>
            <Typography>DeBug</Typography>
            <Switch
              name={"theme_debug"}
              checked={webSiteValues.theme_debug === 1}
              onChange={handleSwitchChange}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#2E7D32",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#2E7D32",
                },
              }}
            />
          </Stack>
          }
        </Box>
      </Box>
    </>
  );
}

export default Features;
